import {isAxiosError} from "axios"
import {
  type CreateDestructionAct,
  type DestructionActStatus,
  type OtkBlank,
  type OtkDestructionAct,
  otkDestructionActSchema,
} from "shared"

import type {ActRequisitesInput} from "@/lib/actRequisites"

export type DestructionActMode = "annulled" | "availableDamaged"

export const destructionActStatusLabels: Record<DestructionActStatus, string> = {
  SUBMISSION_PENDING: "ОЧІКУЄ ВІДПРАВЛЕННЯ",
  ACCEPTED: "ПРИЙНЯТО",
}

// warehouse.md's shared statements, stored verbatim on the act by the
// backend (`destruction-acts-service.ts` writes them alongside the signer
// name/timestamp it reads off the verified КЕП signature — the client never
// sends `byUser`/`atISO` for them, unlike the wireframe's own local mock).
export const ATTESTATION_STATEMENT =
  "Підтверджую, що кожен обраний бланк фактично оглянуто, він є пошкодженим або зіпсованим і перебуває у розпорядженні суб'єкта."

export const MARK_JOURNAL_STATEMENT =
  "Відмітку «Анульовано» проставлено на лицьовому боці кожного обраного бланка, а записи внесено до журналу обліку бланків (пп. 10, 12 Постанови КМУ № 146). Розумію, що АРМ цього не перевіряє і цей запис не є доказом."

export const COPY_DUTY_STATEMENT =
  "Зобов'язуюсь надіслати копію цього акта зазначеному територіальному органу. Розумію, що АРМ копії не надсилає і цей запис не є підтвердженням надсилання."

/**
 * Builds the exact `CreateDestructionAct` shape sent as JSON in the request
 * body and verified against the detached КЕП signature. The dialog signs
 * `JSON.stringify` of this object verbatim, so its fields must match
 * `createDestructionActSchema` in `otk-destruction-act.ts`.
 */
export function buildCreateDestructionActPayload({
  mode,
  blanks,
  reason,
  requisites,
  territorialBody,
}: {
  mode: DestructionActMode
  blanks: Pick<OtkBlank, "id" | "series" | "number">[]
  reason: string
  requisites: ActRequisitesInput
  territorialBody: string
}): CreateDestructionAct {
  return {
    items: blanks.map(({id, series, number}) => ({blankId: id, series, number})),
    reason,
    attestationStatement: ATTESTATION_STATEMENT,
    actDate: requisites.actDate,
    dateFrom: requisites.dateFrom,
    dateTo: requisites.dateTo,
    location: requisites.location,
    commissionChair: requisites.chair,
    commissionMembers: requisites.members.filter((m) => m.role.trim() && m.name.trim()),
    destructionPlace: requisites.destructionPlace,
    destructionTime: requisites.destructionTime,
    destructionMethod: requisites.destructionMethod,
    territorialBody,
    copyDutyAcknowledged: true,
    ...(mode === "availableDamaged"
      ? { markJournalAcknowledgementStatement: MARK_JOURNAL_STATEMENT }
      : {}),
  }
}

// otk-destruction-acts.ts's own error codes, both from КЕП verification
// (`kep-verifier.ts`'s `verifyDestructionActSignature`) and from
// `submitDestructionAct` (`destruction-acts-service.ts`) — ported to
// Ukrainian for the dialog's refusal notice, same role
// `destructionActFailureMessages` played for the old client-only mock gate.
const destructionActApiErrorMessages: Record<string, string> = {
  INVALID_KEP_SIGNATURE:
    "Не вдалося перевірити КЕП-підпис. Перевірте носій ключа і повторіть спробу.",
  INVALID_SIGNED_PAYLOAD:
    "Дані акта мають невірний формат. Оновіть сторінку й повторіть спробу.",
  SIGNER_MISMATCH:
    "Підпис належить іншому користувачу. Використайте свій особистий ключ.",
  BLANK_NOT_ELIGIBLE:
    "Один або кілька обраних бланків змінилися або більше не доступні. Оновіть сторінку й повторіть вибір.",
}

/**
 * The act a failed sign/retry call left behind. `destruction-acts-service.ts`
 * commits the act (`SUBMISSION_PENDING`, blanks claimed) before the first
 * registry attempt, so `502 SUBMISSION_FAILED` and `425 SUBMISSION_NOT_READY`
 * both carry the persisted `act` — the act exists and must be retried, not
 * re-signed (re-signing would hit `409 BLANK_NOT_ELIGIBLE`).
 */
export function pendingActFromError(err: unknown): OtkDestructionAct | null {
  if (!isAxiosError(err)) return null
  const parsed = otkDestructionActSchema.safeParse(
    (err.response?.data as { act?: unknown } | undefined)?.act
  )
  return parsed.success ? parsed.data : null
}

/** Extracts a user-facing message from a failed `retryDestructionAct` call. */
export function destructionActRetryErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const status = err.response?.status
    if (status === 425) return "Повторна відправка ще недоступна."
    if (status === 403) return "Потрібне право на керування складом."
  }
  return "Не вдалося відправити акт до ЄДРТЗ. Спробуйте пізніше."
}

/** Extracts a user-facing message from a failed `submitDestructionAct` call. */
export function destructionActSubmitErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const code = (err.response?.data as { error?: string } | undefined)?.error
    if (code && code in destructionActApiErrorMessages) {
      return destructionActApiErrorMessages[code]!
    }
  }
  return "Не вдалося сформувати акт знищення. Спробуйте ще раз."
}
